import { useState, useCallback, useMemo, useEffect } from 'react'
import {
  PageHeader,
  Button,
  Card,
  LoadingState,
  ErrorState,
  EmptyState,
  StatusBadge,
  Modal,
  Textarea,
  useToast,
  Checkbox,
  Tooltip,
} from '@/components/ui'
import {
  CheckSquare,
  Check,
  Calendar,
  RefreshCw,
  CheckCheck,
  Flag,
  ChevronDown,
  ChevronRight,
  RotateCcw,
  Search,
  Users,
  Ticket,
  XCircle,
  Mail,
  Hash,
  Brain,
} from 'lucide-react'
import { format, subDays, startOfWeek, endOfWeek, startOfMonth, endOfMonth } from 'date-fns'
import { de } from 'date-fns/locale'
import { isAxiosError } from 'axios'
import apiClient from '@/lib/api'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useAuthStore } from '@/stores/auth.ts'

// ─── Types ───────────────────────────────────────────────────────────────────

interface LeistungEntry {
  id: number
  employeeId: number
  employeeName: string
  ticketId: number | null
  ticketTitle: string | null
  ticketText: string | null
  ticketStatusId: number | null
  ticketStatusName: string | null
  companyId: number | null
  companyName: string | null
  date: string
  duration: number
  planningType: string
  title: string | null
  text: string | null
  clearanceStatus: string
  booked: boolean
  erpNumber: string | null
  flags: string[]
  issueCount: number
  reviewStatus: string // 'pending' | 'approved' | 'flagged'
  reviewNotes: string | null
  reviewedAt: string | null
  notProcessable: boolean
}

interface LeistungenResponse {
  data: LeistungEntry[]
  total: number
  tenantId: string | null
  tenantName: string
  from: string
  to: string
  settings: { effortThresholdMinutes: number }
}

interface BulkResult {
  supportId: number
  ok: boolean
  status?: string
  error?: string
  errorCode?: string
  tanssWarning?: string
  clearanceStatusAfter?: string
}

interface AiCheckResult {
  result: 'BESTANDEN' | 'BEANSTANDET'
  confidence: number
  reason: string
  isError?: boolean
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function stripHtml(html: string | null | undefined): string {
  if (!html) return ''
  return html
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"')
    .replace(/&#039;/gi, "'")
    .replace(/\s+/g, ' ')
    .trim()
}

const FLAG_LABELS: Record<string, { label: string; color: string }> = {
  no_text: { label: 'Kein Text', color: 'bg-yellow-100 text-yellow-800' },
  high_effort: { label: 'Hoher Aufwand', color: 'bg-orange-100 text-orange-800' },
  no_material: { label: 'Kein Material', color: 'bg-blue-100 text-blue-800' },
  inconsistent_type: { label: 'Inkons. Typ', color: 'bg-red-100 text-red-800' },
}

function formatMinutes(minutes: number) {
  const h = Math.floor(minutes / 60)
  const m = minutes % 60
  return m > 0 ? `${h}h ${m}min` : `${h}h`
}

// ─── API calls ────────────────────────────────────────────────────────────────

async function fetchLeistungen(from: string, to: string): Promise<LeistungenResponse> {
  const res = await apiClient.get<LeistungenResponse>(`/dispo/leistungen?from=${from}&to=${to}`)
  return res.data
}

async function approveSingle(supportId: number, overrideReason?: string): Promise<{ tanssWarning?: string }> {
  const res = await apiClient.post(`/dispo/leistungen/${supportId}/approve`, { overrideReason })
  return res.data as { tanssWarning?: string }
}

async function revokeSingle(supportId: number): Promise<{ tanssWarning?: string }> {
  const res = await apiClient.post(`/dispo/leistungen/${supportId}/revoke`, {})
  return res.data as { tanssWarning?: string }
}

async function flagSingle(supportId: number, notes: string, employeeId?: number) {
  await apiClient.post(`/dispo/leistungen/${supportId}/flag`, { notes, employeeId })
}

async function unflagSingle(supportId: number) {
  await apiClient.post(`/dispo/leistungen/${supportId}/unflag`, {})
}

async function bulkApprove(supportIds: number[], overrideReason?: string): Promise<{ results: BulkResult[]; successCount: number }> {
  const res = await apiClient.post('/dispo/leistungen/bulk-approve', { supportIds, overrideReason })
  return res.data
}

async function bulkRevoke(supportIds: number[]): Promise<{ results: BulkResult[]; successCount: number }> {
  const res = await apiClient.post('/dispo/leistungen/bulk-revoke', { supportIds })
  return res.data
}

async function customerApproveAll(companyId: number, supportIds: number[]): Promise<{ results: BulkResult[]; successCount: number }> {
  const res = await apiClient.post('/dispo/leistungen/customer-approve-all', { companyId, supportIds })
  return res.data
}

// ─── Selection state helpers ─────────────────────────────────────────────────

type CheckState = 'none' | 'some' | 'all'

function getGroupCheckState(selectedIds: Set<number>, entries: LeistungEntry[]): CheckState {
  const selectable = entries.filter((e) => !e.notProcessable).map((e) => e.id)
  if (selectable.length === 0) return 'none'
  const selected = selectable.filter((id) => selectedIds.has(id))
  if (selected.length === 0) return 'none'
  if (selected.length === selectable.length) return 'all'
  return 'some'
}

// ─── Flag Badges ─────────────────────────────────────────────────────────────

function FlagBadges({ flags }: { flags: string[] }) {
  if (flags.length === 0) return <span className="text-green-600 text-xs">OK</span>
  return (
    <div className="flex flex-wrap gap-1">
      {flags.map((f) => {
        const cfg = FLAG_LABELS[f] ?? { label: f, color: 'bg-gray-100 text-gray-700' }
        return (
          <span key={f} className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${cfg.color}`}>
            {cfg.label}
          </span>
        )
      })}
    </div>
  )
}

// ─── Leistung Row ─────────────────────────────────────────────────────────────

interface LeistungRowProps {
  entry: LeistungEntry
  selected: boolean
  onSelect: (id: number, checked: boolean) => void
  onApprove: (entry: LeistungEntry) => void
  onRevoke: (entry: LeistungEntry) => void
  onFlag: (entry: LeistungEntry) => void
  onUnflag: (entry: LeistungEntry) => void
  actionLoading: boolean
}

function LeistungRow({ entry, selected, onSelect, onApprove, onRevoke, onFlag, onUnflag, actionLoading }: LeistungRowProps) {
  const isPending = entry.reviewStatus === 'pending'
  const isApproved = entry.reviewStatus === 'approved'
  const isFlagged = entry.reviewStatus === 'flagged'
  const canRevoke = isApproved && !entry.booked && !entry.erpNumber
  const canUnflag = isFlagged && !entry.booked && !entry.erpNumber
  const descriptionText = stripHtml(entry.text)

  const statusMap: Record<string, string> = {
    approved: 'leistung_approved',
    flagged: 'leistung_flagged',
    pending: 'leistung_pending',
  }

  return (
    <div className="flex items-start gap-3 py-2.5 px-3 hover:bg-gray-50 border-b border-gray-100 last:border-b-0">
      {/* Checkbox */}
      <div className="pt-0.5 shrink-0">
        <Checkbox
          checked={selected}
          disabled={entry.notProcessable}
          onChange={(e) => onSelect(entry.id, e.target.checked)}
        />
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-medium text-sm text-gray-900">{entry.employeeName}</span>
          <span className="text-xs text-gray-500">
            {format(new Date(entry.date), 'dd.MM.yyyy HH:mm', { locale: de })}
          </span>
          <span className="text-xs text-gray-500">
            {formatMinutes(entry.duration)}
          </span>
          {entry.title && (
            <span className="text-sm text-gray-700 truncate max-w-xs">{entry.title}</span>
          )}
        </div>
        {/* Leistungsnummer + TANSS-Link */}
        <div className="flex items-center gap-1 mt-0.5">
          <a
            href={`https://tanss.implec.de/index.php?section=support&sub=view&supportID=${entry.id}`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1 text-xs text-brand-600 hover:underline"
            onClick={(e) => e.stopPropagation()}
          >
            <Hash className="w-3 h-3 shrink-0" />
            Leistung #{entry.id}
          </a>
        </div>
        {descriptionText ? (
          <Tooltip
            content={<span className="whitespace-pre-line text-xs leading-relaxed">{descriptionText}</span>}
            side="top"
            delayDuration={200}
          >
            <p className="text-xs text-gray-600 line-clamp-1 mt-0.5 cursor-help">{descriptionText}</p>
          </Tooltip>
        ) : (
          <p className="text-xs text-gray-400 italic mt-0.5">Keine Leistungsbeschreibung vorhanden</p>
        )}
        <div className="flex flex-wrap items-center gap-2 mt-1">
          <StatusBadge status={statusMap[entry.reviewStatus] ?? entry.reviewStatus} />
          <FlagBadges flags={entry.flags} />
          {entry.booked && (
            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600">
              Abgerechnet
            </span>
          )}
          {entry.erpNumber && (
            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-700">
              SAP: {entry.erpNumber}
            </span>
          )}
          {entry.reviewNotes && (
            <span className="text-xs text-gray-500 italic" title={entry.reviewNotes}>
              Notiz: {entry.reviewNotes.length > 40 ? `${entry.reviewNotes.substring(0, 40)}…` : entry.reviewNotes}
            </span>
          )}
        </div>
      </div>

      {/* Actions */}
      <div className="flex gap-1 shrink-0">
        {isPending && (
          <>
            <Button
              variant="primary"
              size="xs"
              disabled={actionLoading}
              onClick={() => onApprove(entry)}
              title="Freigeben"
            >
              <CheckSquare className="w-3.5 h-3.5" />
            </Button>
            <Button
              variant="ghost"
              size="xs"
              disabled={actionLoading}
              onClick={() => onFlag(entry)}
              title="Beanstanden"
            >
              <Flag className="w-3.5 h-3.5" />
            </Button>
          </>
        )}
        {isApproved && (
          <Button
            variant="ghost"
            size="xs"
            disabled={!canRevoke || actionLoading}
            onClick={() => onRevoke(entry)}
            title={canRevoke ? 'Freigabe zurücknehmen' : 'Freigabe kann nicht zurückgenommen werden (bereits abgerechnet oder an SAP übertragen)'}
          >
            <RotateCcw className="w-3.5 h-3.5" />
          </Button>
        )}
        {isFlagged && (
          <Button
            variant="ghost"
            size="xs"
            disabled={!canUnflag || actionLoading}
            onClick={() => onUnflag(entry)}
            title={canUnflag ? 'Beanstandung zurücknehmen' : 'Beanstandung kann nicht zurückgenommen werden (bereits abgerechnet oder an SAP übertragen)'}
          >
            <XCircle className="w-3.5 h-3.5" />
          </Button>
        )}
      </div>
    </div>
  )
}

// ─── Ticket Group ─────────────────────────────────────────────────────────────

interface TicketGroupProps {
  ticketId: number | null
  entries: LeistungEntry[]
  selectedIds: Set<number>
  onSelectTicket: (ticketId: number | null, checked: boolean) => void
  onSelectEntry: (id: number, checked: boolean) => void
  onApprove: (entry: LeistungEntry) => void
  onRevoke: (entry: LeistungEntry) => void
  onFlag: (entry: LeistungEntry) => void
  onUnflag: (entry: LeistungEntry) => void
  actionLoading: boolean
  onAiCheck?: (ticketId: number) => void
  aiCheckLoading?: boolean
  aiCheckResult?: AiCheckResult | null
}

function TicketGroup({
  ticketId,
  entries,
  selectedIds,
  onSelectTicket,
  onSelectEntry,
  onApprove,
  onRevoke,
  onFlag,
  onUnflag,
  actionLoading,
  onAiCheck,
  aiCheckLoading,
  aiCheckResult,
}: TicketGroupProps) {
  const [expanded, setExpanded] = useState(true)

  const checkState = getGroupCheckState(selectedIds, entries)

  const pendingCount = entries.filter((e) => e.reviewStatus === 'pending').length
  const cleanPendingCount = entries.filter((e) => e.reviewStatus === 'pending' && e.issueCount === 0).length
  const approvedCount = entries.filter((e) => e.reviewStatus === 'approved').length

  // Ticket title, text and status from first entry (all entries in group share same ticketId)
  const ticketTitle = ticketId !== null ? (entries[0]?.ticketTitle ?? null) : null
  const ticketText = ticketId !== null ? stripHtml(entries[0]?.ticketText) : ''
  const ticketStatusName = ticketId !== null ? (entries[0]?.ticketStatusName ?? null) : null

  return (
    <div className="border border-gray-100 rounded mb-1">
      {/* Ticket header */}
      <div
        className="flex items-start gap-2 px-3 py-2 cursor-pointer hover:bg-gray-50 select-none"
        onClick={() => setExpanded((e) => !e)}
      >
        <div className="pt-0.5 shrink-0" onClick={(e) => e.stopPropagation()}>
          <Checkbox
            checked={checkState === 'all'}
            indeterminate={checkState === 'some'}
            onChange={(e) => onSelectTicket(ticketId, e.target.checked)}
          />
        </div>
        <div className="pt-0.5 shrink-0">
          {expanded ? <ChevronDown className="w-3.5 h-3.5 text-gray-400" /> : <ChevronRight className="w-3.5 h-3.5 text-gray-400" />}
        </div>
        <Ticket className="w-3.5 h-3.5 text-gray-400 shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            {ticketId ? (
              <a
                href={`https://tanss.implec.de/index.php?section=bug&sub=view&bugID=${ticketId}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm font-medium text-brand-600 hover:text-brand-700 hover:underline shrink-0"
                onClick={(e) => e.stopPropagation()}
              >
                #{ticketId}
              </a>
            ) : (
              <span className="text-sm font-medium text-gray-500 shrink-0">Ohne Ticket</span>
            )}
            {ticketTitle && (
              <span className="text-sm text-gray-800 font-medium truncate">{ticketTitle}</span>
            )}
            {ticketStatusName && (
              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600 shrink-0">
                {ticketStatusName}
              </span>
            )}
            <span className="text-xs text-gray-400 ml-auto shrink-0 flex items-center gap-2">
              <span>{entries.length} Leistung(en)</span>
              {cleanPendingCount > 0 && <span className="text-green-600 font-medium">{cleanPendingCount} ohne Fehler</span>}
              {(pendingCount - cleanPendingCount) > 0 && <span className="text-yellow-600 font-medium">{pendingCount - cleanPendingCount} mit Fehlern</span>}
              {approvedCount > 0 && <span className="text-green-600">{approvedCount} freigegeben</span>}
            </span>
            {ticketId !== null && onAiCheck && (
              <div onClick={(e) => e.stopPropagation()} className="shrink-0">
                {aiCheckResult && !aiCheckResult.isError ? (
                  <Tooltip
                    content={<span className="text-xs whitespace-pre-line max-w-xs block">{aiCheckResult.reason}</span>}
                    side="left"
                    delayDuration={150}
                  >
                    <span className="cursor-help inline-flex">
                      <StatusBadge
                        status={aiCheckResult.result === 'BESTANDEN' ? 'ai_bestanden' : 'ai_beanstandet'}
                        label={`KI: ${aiCheckResult.result === 'BESTANDEN' ? 'Bestanden' : 'Beanstandet'} · ${aiCheckResult.confidence}%`}
                      />
                    </span>
                  </Tooltip>
                ) : (
                  <Button
                    variant="ghost"
                    size="xs"
                    disabled={aiCheckLoading}
                    onClick={() => onAiCheck(ticketId)}
                  >
                    <Brain className={`w-3.5 h-3.5 mr-1 ${aiCheckLoading ? 'animate-pulse' : ''}`} />
                    {aiCheckLoading ? 'Prüft …' : 'KI prüfen'}
                  </Button>
                )}
              </div>
            )}
          </div>
          {ticketText && (
            <p className="text-xs text-gray-500 line-clamp-2 mt-0.5">{ticketText}</p>
          )}
        </div>
      </div>

      {/* Entries */}
      {expanded && (
        <div className="ml-8 border-t border-gray-100">
          {entries.map((entry) => (
            <LeistungRow
              key={entry.id}
              entry={entry}
              selected={selectedIds.has(entry.id)}
              onSelect={onSelectEntry}
              onApprove={onApprove}
              onRevoke={onRevoke}
              onFlag={onFlag}
              onUnflag={onUnflag}
              actionLoading={actionLoading}
            />
          ))}
        </div>
      )}
    </div>
  )
}

// ─── Customer Group ───────────────────────────────────────────────────────────

interface CustomerGroupProps {
  companyId: number | null
  companyName: string | null
  tickets: Map<number | null, LeistungEntry[]>
  allEntries: LeistungEntry[]
  selectedIds: Set<number>
  onSelectCustomer: (companyId: number | null, checked: boolean) => void
  onSelectTicket: (companyId: number | null, ticketId: number | null, checked: boolean) => void
  onSelectEntry: (id: number, checked: boolean) => void
  onApprove: (entry: LeistungEntry) => void
  onRevoke: (entry: LeistungEntry) => void
  onFlag: (entry: LeistungEntry) => void
  onUnflag: (entry: LeistungEntry) => void
  onCustomerApproveAll: (companyId: number | null) => void
  actionLoading: boolean
  onAiCheck?: (ticketId: number) => void
  aiCheckLoadingIds?: Set<number>
  aiCheckResults?: Map<number, AiCheckResult>
}

function CustomerGroup({
  companyId,
  companyName,
  tickets,
  allEntries,
  selectedIds,
  onSelectCustomer,
  onSelectTicket,
  onSelectEntry,
  onApprove,
  onRevoke,
  onFlag,
  onUnflag,
  onCustomerApproveAll,
  actionLoading,
  onAiCheck,
  aiCheckLoadingIds,
  aiCheckResults,
}: CustomerGroupProps) {
  const [expanded, setExpanded] = useState(true)

  const checkState = getGroupCheckState(selectedIds, allEntries)

  const pendingProcessable = allEntries.filter((e) => e.reviewStatus === 'pending' && !e.notProcessable)
  const cleanPendingProcessable = allEntries.filter((e) => e.reviewStatus === 'pending' && !e.notProcessable && e.issueCount === 0)
  const approvedCount = allEntries.filter((e) => e.reviewStatus === 'approved').length
  const flaggedCount = allEntries.filter((e) => e.reviewStatus === 'flagged').length

  return (
    <div className="border border-gray-200 rounded-lg mb-3 bg-white shadow-sm">
      {/* Customer header */}
      <div
        className="flex items-center gap-2 px-4 py-3 cursor-pointer hover:bg-gray-50 rounded-t-lg select-none"
        onClick={() => setExpanded((e) => !e)}
      >
        <div onClick={(e) => e.stopPropagation()}>
          <Checkbox
            checked={checkState === 'all'}
            indeterminate={checkState === 'some'}
            onChange={(e) => onSelectCustomer(companyId, e.target.checked)}
          />
        </div>
        {expanded ? <ChevronDown className="w-4 h-4 text-gray-500 shrink-0" /> : <ChevronRight className="w-4 h-4 text-gray-500 shrink-0" />}
        <Users className="w-4 h-4 text-brand-600 shrink-0" />
        <span className="font-semibold text-gray-800 flex-1">
          {companyId
            ? `#${companyId}${companyName ? ` ${companyName}` : ''}`
            : 'Ohne Kundenzuordnung'}
        </span>
        <div className="flex items-center gap-3 text-xs text-gray-500">
          <span>{allEntries.length} Leistung(en)</span>
          {cleanPendingProcessable.length > 0 && <span className="text-green-600 font-medium">{cleanPendingProcessable.length} ohne Fehler</span>}
          {(pendingProcessable.length - cleanPendingProcessable.length) > 0 && <span className="text-yellow-600 font-medium">{pendingProcessable.length - cleanPendingProcessable.length} mit Fehlern</span>}
          {approvedCount > 0 && <span className="text-green-600">{approvedCount} freigegeben</span>}
          {flaggedCount > 0 && <span className="text-red-600">{flaggedCount} beanstandet</span>}
        </div>
        {cleanPendingProcessable.length > 0 && (
          <div onClick={(e) => e.stopPropagation()}>
            <Button
              variant="primary"
              size="xs"
              disabled={actionLoading}
              onClick={() => onCustomerApproveAll(companyId)}
              title={`${cleanPendingProcessable.length} Leistung(en) ohne Qualitätsfehler freigeben`}
            >
              <CheckCheck className="w-3.5 h-3.5 mr-1" />
              Alle freigeben
            </Button>
          </div>
        )}
      </div>

      {/* Ticket groups */}
      {expanded && (
        <div className="px-4 pb-3 pt-1">
          {Array.from(tickets.entries()).map(([ticketId, ticketEntries]) => (
            <TicketGroup
              key={ticketId ?? 'no-ticket'}
              ticketId={ticketId}
              entries={ticketEntries}
              selectedIds={selectedIds}
              onSelectTicket={(tId, checked) => onSelectTicket(companyId, tId, checked)}
              onSelectEntry={onSelectEntry}
              onApprove={onApprove}
              onRevoke={onRevoke}
              onFlag={onFlag}
              onUnflag={onUnflag}
              actionLoading={actionLoading}
              onAiCheck={onAiCheck}
              aiCheckLoading={ticketId !== null ? aiCheckLoadingIds?.has(ticketId) : false}
              aiCheckResult={ticketId !== null ? (aiCheckResults?.get(ticketId) ?? null) : null}
            />
          ))}
        </div>
      )}
    </div>
  )
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export function DispoLeistungsfreigabePage() {
  const { toast } = useToast()
  const queryClient = useQueryClient()
  const currentUser = useAuthStore((s) => s.user)
  const isAdmin = currentUser?.role === 'admin'

  // Date range state
  const [dateFrom, setDateFrom] = useState(() => format(new Date(), 'yyyy-MM-dd'))
  const [dateTo, setDateTo] = useState(() => format(new Date(), 'yyyy-MM-dd'))

  // Selection state
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set())

  // Per-ticket AI check state (admin only)
  const [aiCheckLoadingIds, setAiCheckLoadingIds] = useState<Set<number>>(new Set())
  const [aiCheckResults, setAiCheckResults] = useState<Map<number, AiCheckResult>>(new Map())

  // Search
  const [searchQuery, setSearchQuery] = useState('')

  // Modals
  const [flagModal, setFlagModal] = useState<{ entry: LeistungEntry } | null>(null)
  const [flagNotes, setFlagNotes] = useState('')
  const [flagLoading, setFlagLoading] = useState(false)

  const queryKey = ['dispo-leistungen', dateFrom, dateTo]

  const { data, isLoading, error, refetch } = useQuery({
    queryKey,
    queryFn: () => fetchLeistungen(dateFrom, dateTo),
    staleTime: 30_000,
    refetchOnWindowFocus: false,
  })

  const invalidate = useCallback(() => {
    queryClient.invalidateQueries({ queryKey })
  }, [queryClient, queryKey])

  const triggerAiCheck = useCallback(async (ticketId: number) => {
    const tenantId = data?.tenantId
    if (!tenantId) {
      toast({ title: 'Kein Mandant gefunden', tone: 'error' })
      return
    }
    setAiCheckLoadingIds((prev) => new Set([...prev, ticketId]))
    try {
      const res = await apiClient.post<{ result: string; confidence: number; reason: string; isError?: boolean }>(
        `/dispo/clearance/tickets/${ticketId}/ai-check`,
        {},
        { headers: { 'X-Tenant-Id': tenantId } },
      )
      const result: AiCheckResult = {
        result: res.data.result as 'BESTANDEN' | 'BEANSTANDET',
        confidence: res.data.confidence,
        reason: res.data.reason,
        isError: res.data.isError,
      }
      setAiCheckResults((prev) => new Map([...prev, [ticketId, result]]))
      if (result.isError) {
        toast({ title: 'KI-Prüfung: Technischer Fehler', description: result.reason, tone: 'error' })
      } else {
        toast({
          title: result.result === 'BESTANDEN' ? 'KI: Bestanden' : 'KI: Beanstandet',
          description: `Confidence: ${result.confidence}%`,
          tone: result.result === 'BESTANDEN' ? 'success' : 'warning',
        })
      }
    } catch (err) {
      const msg = isAxiosError(err) ? (err.response?.data as { message?: string })?.message ?? err.message : String(err)
      toast({ title: 'KI-Prüfung fehlgeschlagen', description: msg, tone: 'error' })
    } finally {
      setAiCheckLoadingIds((prev) => {
        const next = new Set(prev)
        next.delete(ticketId)
        return next
      })
    }
  }, [data, toast])

  // Load persisted AI results for all visible tickets on data change (admin only)
  useEffect(() => {
    if (!isAdmin || !data?.tenantId || !data.data?.length) {
      setAiCheckResults(new Map())
      return
    }
    const ticketIds = [
      ...new Set(
        data.data
          .map((e) => e.ticketId)
          .filter((id): id is number => id !== null),
      ),
    ]
    if (!ticketIds.length) {
      setAiCheckResults(new Map())
      return
    }
    const tenantId = data.tenantId
    void (async () => {
      const settled = await Promise.allSettled(
        ticketIds.map(async (ticketId) => {
          const res = await apiClient.get<{ result: string; confidence: number; reason: string } | null>(
            `/dispo/clearance/tickets/${ticketId}/ai-result`,
            { headers: { 'X-Tenant-Id': tenantId } },
          )
          return { ticketId, payload: res.data }
        }),
      )
      setAiCheckResults(() => {
        const next = new Map<number, AiCheckResult>()
        for (const item of settled) {
          if (item.status === 'fulfilled' && item.value.payload) {
            const r = item.value.payload
            next.set(item.value.ticketId, {
              result: r.result as 'BESTANDEN' | 'BEANSTANDET',
              confidence: r.confidence,
              reason: r.reason,
            })
          }
        }
        return next
      })
    })()
  }, [data, isAdmin])

  // ─ Action mutations ─

  const [actionLoading, setActionLoading] = useState(false)

  const handleApprove = useCallback(async (entry: LeistungEntry) => {
    setActionLoading(true)
    try {
      const result = await approveSingle(entry.id)
      toast({ title: 'Freigegeben', tone: 'success' })
      if (result.tanssWarning) {
        toast({ title: 'TANSS-Sync unvollständig', description: result.tanssWarning, tone: 'warning' })
      }
      invalidate()
    } catch (err) {
      const msg = isAxiosError(err) ? (err.response?.data as { message?: string })?.message ?? err.message : String(err)
      toast({ title: 'Freigabe fehlgeschlagen', description: msg, tone: 'error' })
    } finally {
      setActionLoading(false)
    }
  }, [invalidate, toast])

  const handleRevoke = useCallback(async (entry: LeistungEntry) => {
    setActionLoading(true)
    try {
      const result = await revokeSingle(entry.id)
      toast({ title: 'Freigabe zurückgenommen', tone: 'success' })
      if (result.tanssWarning) {
        toast({ title: 'TANSS-Sync unvollständig', description: result.tanssWarning, tone: 'warning' })
      }
      invalidate()
    } catch (err) {
      const msg = isAxiosError(err) ? (err.response?.data as { message?: string })?.message ?? err.message : String(err)
      toast({ title: 'Rücknahme fehlgeschlagen', description: msg, tone: 'error' })
    } finally {
      setActionLoading(false)
    }
  }, [invalidate, toast])

  const handleFlag = useCallback(async () => {
    if (!flagModal || !flagNotes.trim()) return
    setFlagLoading(true)
    try {
      await flagSingle(flagModal.entry.id, flagNotes.trim(), flagModal.entry.employeeId || undefined)
      toast({ title: 'Beanstandet', tone: 'success' })
      invalidate()
      setFlagModal(null)
      setFlagNotes('')
    } catch (err) {
      const msg = isAxiosError(err) ? (err.response?.data as { message?: string })?.message ?? (err as Error).message : String(err)
      toast({ title: 'Beanstandung fehlgeschlagen', description: msg, tone: 'error' })
    } finally {
      setFlagLoading(false)
    }
  }, [flagModal, flagNotes, invalidate, toast])

  const handleUnflag = useCallback(async (entry: LeistungEntry) => {
    setActionLoading(true)
    try {
      await unflagSingle(entry.id)
      toast({ title: 'Beanstandung zurückgenommen', tone: 'success' })
      invalidate()
    } catch (err) {
      const msg = isAxiosError(err) ? (err.response?.data as { message?: string })?.message ?? err.message : String(err)
      toast({ title: 'Beanstandungsrücknahme fehlgeschlagen', description: msg, tone: 'error' })
    } finally {
      setActionLoading(false)
    }
  }, [invalidate, toast])

  const handleBulkApprove = useCallback(async () => {
    const ids = Array.from(selectedIds as Set<number>)
    if (ids.length === 0) return
    setActionLoading(true)
    try {
      const result = await bulkApprove(ids)
      toast({ title: `${result.successCount} von ${ids.length} Leistungen freigegeben`, tone: result.successCount === ids.length ? 'success' : 'warning' })
      const tanssFailed = result.results.filter((r) => r.tanssWarning).length
      if (tanssFailed > 0) {
        const warnings = result.results.filter((r) => r.tanssWarning).map((r) => `#${r.supportId}: ${r.tanssWarning}`).join('\n')
        toast({ title: 'TANSS-Freigabe unvollständig', description: warnings, tone: 'warning' })
      }
      const notCleared = result.results.filter((r) => r.ok && r.clearanceStatusAfter && r.clearanceStatusAfter !== 'CLEARED')
      if (notCleared.length > 0) {
        toast({
          title: 'TANSS-Status nicht gesetzt',
          description: `${notCleared.length} Leistung(en) haben clearanceStatus "${notCleared[0]?.clearanceStatusAfter}" statt CLEARED — bitte Serverlog prüfen.`,
          tone: 'warning',
        })
      }
      setSelectedIds(new Set())
      invalidate()
    } catch (err) {
      const msg = isAxiosError(err) ? (err.response?.data as { message?: string })?.message ?? err.message : String(err)
      toast({ title: 'Sammelfreigabe fehlgeschlagen', description: msg, tone: 'error' })
    } finally {
      setActionLoading(false)
    }
  }, [selectedIds, invalidate, toast])

  const handleBulkRevoke = useCallback(async () => {
    const ids = Array.from(selectedIds as Set<number>)
    if (ids.length === 0) return
    setActionLoading(true)
    try {
      const result = await bulkRevoke(ids)
      toast({ title: `${result.successCount} von ${ids.length} Freigaben zurückgenommen`, tone: result.successCount === ids.length ? 'success' : 'warning' })
      const tanssFailed = result.results.filter((r) => r.tanssWarning).length
      if (tanssFailed > 0) {
        toast({ title: 'TANSS-Sync unvollständig', description: `${tanssFailed} Leistung(en) konnten nicht in TANSS zurückgesetzt werden.`, tone: 'warning' })
      }
      setSelectedIds(new Set())
      invalidate()
    } catch (err) {
      const msg = isAxiosError(err) ? (err.response?.data as { message?: string })?.message ?? err.message : String(err)
      toast({ title: 'Sammel-Rücknahme fehlgeschlagen', description: msg, tone: 'error' })
    } finally {
      setActionLoading(false)
    }
  }, [selectedIds, invalidate, toast])

  const handleCustomerApproveAll = useCallback(async (companyId: number | null) => {
    const entries = data?.data ?? []
    const processable = entries.filter(
      (e) => e.companyId === companyId && e.reviewStatus === 'pending' && !e.notProcessable && e.issueCount === 0,
    )
    if (processable.length === 0) return
    setActionLoading(true)
    try {
      const result = await customerApproveAll(companyId ?? 0, processable.map((e) => e.id))
      toast({ title: `${result.successCount} Leistungen für Kunden freigegeben`, tone: result.successCount === processable.length ? 'success' : 'warning' })
      const tanssFailed = result.results.filter((r) => r.tanssWarning).length
      if (tanssFailed > 0) {
        toast({ title: 'TANSS-Sync unvollständig', description: `${tanssFailed} Leistung(en) konnten nicht in TANSS synchronisiert werden.`, tone: 'warning' })
      }
      invalidate()
    } catch (err) {
      const msg = isAxiosError(err) ? (err.response?.data as { message?: string })?.message ?? err.message : String(err)
      toast({ title: 'Kundenfreigabe fehlgeschlagen', description: msg, tone: 'error' })
    } finally {
      setActionLoading(false)
    }
  }, [data, invalidate, toast])

  const handleApproveAllGlobal = useCallback(async () => {
    const allEntries = data?.data ?? []
    const processable = allEntries.filter(
      (e) => e.reviewStatus === 'pending' && !e.notProcessable && e.issueCount === 0,
    )
    if (processable.length === 0) return
    setActionLoading(true)
    try {
      const result = await bulkApprove(processable.map((e) => e.id))
      toast({ title: `${result.successCount} von ${processable.length} Leistungen freigegeben`, tone: result.successCount === processable.length ? 'success' : 'warning' })
      const tanssFailed = result.results.filter((r) => r.tanssWarning).length
      if (tanssFailed > 0) {
        toast({ title: 'TANSS-Sync unvollständig', description: `${tanssFailed} Leistung(en) konnten nicht in TANSS synchronisiert werden.`, tone: 'warning' })
      }
      invalidate()
    } catch (err) {
      const msg = isAxiosError(err) ? (err.response?.data as { message?: string })?.message ?? err.message : String(err)
      toast({ title: 'Globalfreigabe fehlgeschlagen', description: msg, tone: 'error' })
    } finally {
      setActionLoading(false)
    }
  }, [data, invalidate, toast])

  // ─ Selection handlers ─

  const handleSelectEntry = useCallback((id: number, checked: boolean) => {
    setSelectedIds((prev: Set<number>) => {
      const next = new Set(prev)
      if (checked) next.add(id)
      else next.delete(id)
      return next
    })
  }, [])

  const handleSelectTicket = useCallback((companyId: number | null, ticketId: number | null, checked: boolean) => {
    const entries = data?.data ?? []
    const ticketEntries = entries.filter(
      (e: LeistungEntry) => e.companyId === companyId && e.ticketId === ticketId && !e.notProcessable,
    )
    setSelectedIds((prev: Set<number>) => {
      const next = new Set(prev)
      ticketEntries.forEach((e: LeistungEntry) => {
        if (checked) next.add(e.id)
        else next.delete(e.id)
      })
      return next
    })
  }, [data])

  const handleSelectCustomer = useCallback((companyId: number | null, checked: boolean) => {
    const entries = data?.data ?? []
    const customerEntries = entries.filter((e: LeistungEntry) => e.companyId === companyId && !e.notProcessable)
    setSelectedIds((prev) => {
      const next = new Set(prev)
      customerEntries.forEach((e) => {
        if (checked) next.add(e.id)
        else next.delete(e.id)
      })
      return next
    })
  }, [data])

  // ─ Date range presets ─

  function setPreset(preset: 'today' | 'yesterday' | 'week' | 'month') {
    const now = new Date()
    if (preset === 'today') {
      setDateFrom(format(now, 'yyyy-MM-dd'))
      setDateTo(format(now, 'yyyy-MM-dd'))
    } else if (preset === 'yesterday') {
      const y = subDays(now, 1)
      setDateFrom(format(y, 'yyyy-MM-dd'))
      setDateTo(format(y, 'yyyy-MM-dd'))
    } else if (preset === 'week') {
      setDateFrom(format(startOfWeek(now, { locale: de }), 'yyyy-MM-dd'))
      setDateTo(format(endOfWeek(now, { locale: de }), 'yyyy-MM-dd'))
    } else if (preset === 'month') {
      setDateFrom(format(startOfMonth(now), 'yyyy-MM-dd'))
      setDateTo(format(endOfMonth(now), 'yyyy-MM-dd'))
    }
  }

  // ─ Tree data structure ─

  const { groupedData, filteredEntries, stats } = useMemo(() => {
    const entries = data?.data ?? []

    // Apply search filter
    const q = searchQuery.trim().toLowerCase()
    const filtered = q
      ? entries.filter(
          (e) =>
            e.employeeName.toLowerCase().includes(q) ||
            (e.title ?? '').toLowerCase().includes(q) ||
            (e.text ?? '').toLowerCase().includes(q) ||
            (e.companyName ?? '').toLowerCase().includes(q) ||
            String(e.ticketId ?? '').includes(q),
        )
      : entries

    // Group: companyId → ticketId → entries[]
    const byCustomer = new Map<number | null, Map<number | null, LeistungEntry[]>>()

    for (const e of filtered) {
      if (!byCustomer.has(e.companyId)) byCustomer.set(e.companyId, new Map())
      const byTicket = byCustomer.get(e.companyId)!
      if (!byTicket.has(e.ticketId)) byTicket.set(e.ticketId, [])
      byTicket.get(e.ticketId)!.push(e)
    }

    const withoutErrorsCount = filtered.filter((e) => e.issueCount === 0 && e.reviewStatus !== 'flagged').length
    const beanstandetCount = filtered.filter((e) => e.issueCount > 0 || e.reviewStatus === 'flagged').length
    const approvedCount = filtered.filter((e) => e.reviewStatus === 'approved').length

    return {
      groupedData: byCustomer,
      filteredEntries: filtered,
      stats: { withoutErrorsCount, beanstandetCount, approvedCount, total: filtered.length },
    }
  }, [data, searchQuery])

  const approveAllCount = useMemo(() => {
    const allEntries = data?.data ?? []
    return allEntries.filter((e) => e.reviewStatus === 'pending' && !e.notProcessable && e.issueCount === 0).length
  }, [data])

  // Selected entries split by action type
  const selectedApprovedIds = useMemo(() => {
    const entries = data?.data ?? []
    return Array.from(selectedIds).filter((id) => {
      const e = entries.find((x) => x.id === id)
      return e?.reviewStatus === 'approved' && !e.booked && !e.erpNumber
    })
  }, [selectedIds, data])

  const selectedPendingIds = useMemo(() => {
    const entries = data?.data ?? []
    return Array.from(selectedIds).filter((id) => {
      const e = entries.find((x) => x.id === id)
      return e?.reviewStatus === 'pending' && !e.notProcessable
    })
  }, [selectedIds, data])

  const tenantName = data?.tenantName ?? 'Mandant'

  return (
    <div className="p-6 space-y-4">
      <PageHeader
        title="Leistungsfreigabe"
        actions={
          <div className="flex items-center gap-2 flex-wrap">
            <div className="flex items-center gap-1.5 text-sm text-gray-600">
              <Calendar className="w-4 h-4 shrink-0" />
              <input
                type="date"
                value={dateFrom}
                max={dateTo}
                onChange={(e) => setDateFrom(e.target.value)}
                className="border border-gray-300 rounded px-2 py-1 text-sm"
              />
              <span className="text-gray-400">–</span>
              <input
                type="date"
                value={dateTo}
                min={dateFrom}
                max={format(new Date(), 'yyyy-MM-dd')}
                onChange={(e) => setDateTo(e.target.value)}
                className="border border-gray-300 rounded px-2 py-1 text-sm"
              />
            </div>
            <Button variant="ghost" size="sm" onClick={() => setPreset('today')}>Heute</Button>
            <Button variant="ghost" size="sm" onClick={() => setPreset('yesterday')}>Gestern</Button>
            <Button variant="ghost" size="sm" onClick={() => setPreset('week')}>Diese Woche</Button>
            <Button variant="ghost" size="sm" onClick={() => setPreset('month')}>Dieser Monat</Button>
            <Button variant="ghost" size="sm" onClick={() => refetch()}>
              <RefreshCw className="w-4 h-4" />
            </Button>
            {approveAllCount > 0 && (
              <Button
                variant="primary"
                size="sm"
                disabled={actionLoading}
                onClick={handleApproveAllGlobal}
              >
                <CheckCheck className="w-4 h-4 mr-1.5" />
                Alle {approveAllCount} Leistungen freigeben
              </Button>
            )}
          </div>
        }
      />

      {/* Summary stats */}
      <div className="grid grid-cols-4 gap-4">
        <Card>
          <Card.Body>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-50 rounded-lg">
                <Check className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <div className="text-2xl font-bold">{stats.withoutErrorsCount}</div>
                <div className="text-sm text-gray-500">Ohne Fehler</div>
              </div>
            </div>
          </Card.Body>
        </Card>
        <Card>
          <Card.Body>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-50 rounded-lg">
                <Mail className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <div className="text-2xl font-bold">{stats.approvedCount}</div>
                <div className="text-sm text-gray-500">Freigegeben</div>
              </div>
            </div>
          </Card.Body>
        </Card>
        <Card>
          <Card.Body>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-50 rounded-lg">
                <Flag className="w-5 h-5 text-red-500" />
              </div>
              <div>
                <div className="text-2xl font-bold">{stats.beanstandetCount}</div>
                <div className="text-sm text-gray-500">Beanstandet</div>
              </div>
            </div>
          </Card.Body>
        </Card>
        <Card>
          <Card.Body>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-50 rounded-lg">
                <CheckSquare className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <div className="text-2xl font-bold">{stats.total}</div>
                <div className="text-sm text-gray-500">Gesamt</div>
              </div>
            </div>
          </Card.Body>
        </Card>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
        <input
          type="text"
          placeholder="Mitarbeiter, Bezeichnung, Kunde oder Ticket suchen…"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/40"
        />
      </div>

      {/* Bulk action bar — sticky so it stays visible when scrolling through long lists */}
      {selectedIds.size > 0 && (
        <div className="sticky top-0 z-sticky bg-gray-50 pb-2">
        <Card className="shadow-sm">
          <Card.Body>
            <div className="flex items-center justify-between flex-wrap gap-3">
              <span className="text-sm text-gray-700">
                {selectedIds.size} Leistung(en) ausgewählt
                {selectedPendingIds.length > 0 && ` · ${selectedPendingIds.length} ausstehend`}
                {selectedApprovedIds.length > 0 && ` · ${selectedApprovedIds.length} freigegeben`}
              </span>
              <div className="flex gap-2">
                {selectedPendingIds.length > 0 && (
                  <Button
                    variant="primary"
                    size="sm"
                    disabled={actionLoading}
                    onClick={handleBulkApprove}
                  >
                    <CheckCheck className="w-4 h-4 mr-1.5" />
                    {selectedPendingIds.length} freigeben
                  </Button>
                )}
                {selectedApprovedIds.length > 0 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={actionLoading}
                    onClick={handleBulkRevoke}
                  >
                    <RotateCcw className="w-4 h-4 mr-1.5" />
                    {selectedApprovedIds.length} Freigaben zurücknehmen
                  </Button>
                )}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setSelectedIds(new Set())}
                >
                  Auswahl aufheben
                </Button>
              </div>
            </div>
          </Card.Body>
        </Card>
        </div>
      )}

      {/* Tree */}
      <div>
        {isLoading ? (
          <LoadingState />
        ) : error ? (
          <ErrorState
            title="Leistungen konnten nicht geladen werden"
            description={(error as Error).message}
            onRetry={() => refetch()}
          />
        ) : stats.total === 0 ? (
          <EmptyState
            icon={<CheckSquare className="w-8 h-8" />}
            title="Keine Leistungen für diesen Zeitraum"
            description="Für den gewählten Zeitraum wurden keine TANSS-Supports gefunden."
          />
        ) : filteredEntries.length === 0 ? (
          <EmptyState
            icon={<Search className="w-8 h-8" />}
            title="Keine Treffer"
            description="Die Suche ergab keine Ergebnisse."
          />
        ) : (
          <>
            {/* Tenant header — only shown when there's a tenant name */}
            <div className="flex items-center gap-2 mb-3 px-1">
              <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{tenantName}</span>
              <div className="flex-1 h-px bg-gray-200" />
              <span className="text-xs text-gray-400">{filteredEntries.length} Leistung(en)</span>
            </div>

            {/* Customer groups */}
            {Array.from(groupedData.entries()).map(([companyId, ticketMap]) => {
              const allCustomerEntries = Array.from(ticketMap.values()).flat()
              const firstEntry = allCustomerEntries[0]
              const companyName = firstEntry?.companyName ?? null

              return (
                <CustomerGroup
                  key={companyId ?? 'no-company'}
                  companyId={companyId}
                  companyName={companyName}
                  tickets={ticketMap}
                  allEntries={allCustomerEntries}
                  selectedIds={selectedIds}
                  onSelectCustomer={handleSelectCustomer}
                  onSelectTicket={handleSelectTicket}
                  onSelectEntry={handleSelectEntry}
                  onApprove={handleApprove}
                  onRevoke={handleRevoke}
                  onFlag={(entry) => {
                    setFlagModal({ entry })
                    setFlagNotes('')
                  }}
                  onUnflag={handleUnflag}
                  onCustomerApproveAll={handleCustomerApproveAll}
                  actionLoading={actionLoading}
                  onAiCheck={isAdmin ? triggerAiCheck : undefined}
                  aiCheckLoadingIds={aiCheckLoadingIds}
                  aiCheckResults={aiCheckResults}
                />
              )
            })}
          </>
        )}
      </div>

      {/* Flag Modal */}
      <Modal
        open={!!flagModal}
        onClose={() => setFlagModal(null)}
        title={`Leistung beanstanden — ${flagModal?.entry.employeeName ?? ''}`}
        footer={
          <div className="flex gap-2 justify-end">
            <Button variant="ghost" onClick={() => setFlagModal(null)}>
              Abbrechen
            </Button>
            <Button
              variant="danger"
              disabled={!flagNotes.trim() || flagLoading}
              onClick={handleFlag}
            >
              <Flag className="w-4 h-4 mr-1.5" />
              Beanstanden
            </Button>
          </div>
        }
      >
        <div className="space-y-3">
          <p className="text-sm text-gray-600">
            Die Leistung wird als beanstandet markiert. Ein TANSS-Chat zur Leistung wird erstellt und der Techniker sowie die Dispo werden benachrichtigt.
          </p>
          <Textarea
            label="Begründung (Pflichtfeld)"
            placeholder="z.B. Stundenanzahl nicht plausibel, bitte korrigieren"
            value={flagNotes}
            onChange={(e) => setFlagNotes(e.target.value)}
            rows={4}
          />
        </div>
      </Modal>
    </div>
  )
}
